import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';

// Register the required Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface SpiderChartProps {
  playerStats: {
    goals: number;
    assists: number;
    minutes_played: number;
    appearances: number;
    yellow_cards: number;
    red_cards: number;
  } | null;
  className?: string;
}

export const SpiderChart: React.FC<SpiderChartProps> = ({ playerStats, className }) => {
  if (!playerStats) {
    return (
      <div className={`flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700 rounded-lg ${className}`}>
        <p className="text-gray-500 dark:text-gray-400">No stats available for spider chart</p>
      </div>
    );
  }
  
  // Check if all relevant stats are zero or no appearances
  const allZero = 
    playerStats.goals === 0 && 
    playerStats.assists === 0 && 
    playerStats.yellow_cards === 0 && 
    playerStats.red_cards === 0 && 
    (playerStats.appearances === 0 || playerStats.minutes_played === 0);
    
  if (allZero) {
    return (
      <div className={`flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-700 rounded-lg ${className}`}>
        <p className="text-gray-500 dark:text-gray-400">No performance data available to display</p>
      </div>
    );
  }

  // Calculate normalized values (per appearance)
  const appearances = Math.max(1,playerStats.appearances) // Avoid division by zero
  const goalsPerAppearance = playerStats.goals / appearances;
  const assistsPerAppearance = playerStats.assists / appearances;
  const minutesPerAppearance = ((playerStats.minutes_played / appearances) / 90);
  const yellowCardsPerAppearance = playerStats.yellow_cards / appearances;
  const redCardsPerAppearance = playerStats.red_cards / appearances;
  
  // Calculate percentages based on reference values
  // This ensures each stat is shown relative to a meaningful maximum
  const goalsPercentage = goalsPerAppearance * 100;
  const assistsPercentage = assistsPerAppearance * 100;
  const minutesPercentage = minutesPerAppearance * 100;
  const yellowCardsPercentage = yellowCardsPerAppearance * 100;
  const redCardsPercentage = redCardsPerAppearance * 100;

  // Use percentages for the chart data to ensure consistent scale
  const data: ChartData<'radar'> = {
    labels: ['Goals %', 'Assists %', 'Minutes %', 'Yellow Cards %', 'Red Cards %'],
    datasets: [
      {
        label: 'Player Stats averaged per appearance',
        data: [
          goalsPercentage,
          assistsPercentage,
          minutesPercentage,
          yellowCardsPercentage,
          redCardsPercentage
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(54, 162, 235, 1)'
      }
    ]
  };

  const options: ChartOptions<'radar'> = {
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(128, 128, 128, 0.2)'
        },
        grid: {
          color: 'rgba(128, 128, 128, 0.2)'
        },
        pointLabels: {
          color: 'rgb(100, 116, 139)',
          font: {
            size: 11
          }
        },
        ticks: {
          backdropColor: 'transparent',
          color: 'rgb(100, 116, 139)',
          showLabelBackdrop: false,
          stepSize: 20,
          // Format the tick labels to show percentages
          callback: (value) => {
            return `${value}`;
          }
        },
        min: 0,
        max: 100,
        suggestedMin: 0,
        suggestedMax: 100
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          boxWidth: 10,
          font: {
            size: 10
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw as number;
            const index = context.dataIndex;
            
            // Return both raw and normalized values with reference to typical values
            switch(index) {
              case 0: return `${label}: ${playerStats.goals} goals (${goalsPerAppearance.toFixed(2)}/app, ${Math.round(value)}% of ref)`;
              case 1: return `${label}: ${playerStats.assists} assists (${assistsPerAppearance.toFixed(2)}/app, ${Math.round(value)}% of ref)`;
              case 2: return `${label}: ${Math.round(minutesPerAppearance)} min/app (${Math.round(value)}% of ref)`;
              case 3: return `${label}: ${playerStats.yellow_cards} yellow cards (${yellowCardsPerAppearance.toFixed(2)}/app, ${Math.round(value)}% of ref)`;
              case 4: return `${label}: ${playerStats.red_cards} red cards (${redCardsPerAppearance.toFixed(2)}/app, ${Math.round(value)}% of ref)`;
              default: return `${label}: ${value}`;
            }
          }
        }
      }
    },
    maintainAspectRatio: false
  };

  return (
    <div className={`${className}`}>
      <div className="h-64">
        <Radar data={data} options={options} />
      </div>
      {/* <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
        <p>Chart shows stats normalized by {playerStats.appearances} appearances</p>
      </div> */}
    </div>
  );
};